import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { QUESTION_TYPES, QUESTION_CONFIGS } from '../../constants/question-types';

/**
 * Toolbar for adding new questions - Redesigned to use a dropdown menu
 */
const QuestionToolbar = ({ onAddQuestion }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, bottom: 'auto' });
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const handleAddQuestion = (questionType) => {
    const config = QUESTION_CONFIGS[questionType].defaultConfig;
    onAddQuestion(questionType, config);
    setIsOpen(false);
  };

  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const MENU_HEIGHT_APPROX = 400; // Estimate for list of questions

      const spaceBelow = viewportHeight - rect.bottom;

      // Center the menu horizontally relative to the button
      const menuWidth = 280;
      const buttonCenter = rect.left + rect.width / 2;
      const left = buttonCenter - menuWidth / 2;

      let newTop = rect.bottom + 12; // Gap
      let newBottom = 'auto';

      // If not enough space below, position above
      if (spaceBelow < MENU_HEIGHT_APPROX) {
        newTop = 'auto';
        newBottom = viewportHeight - rect.top + 12;
      }

      setMenuPosition({
        top: newTop,
        left: left,
        bottom: newBottom
      });
    }
  };

  const toggleMenu = (e) => {
    e.stopPropagation();
    if (!isOpen) {
      updatePosition();
    }
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        buttonRef.current && !buttonRef.current.contains(event.target) &&
        menuRef.current && !menuRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => { if (isOpen) setIsOpen(false); };
    const handleResize = () => { if (isOpen) setIsOpen(false); };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  const questionOptions = Object.values(QUESTION_TYPES).map((type) => {
    const config = QUESTION_CONFIGS[type];
    return {
      type,
      label: config.label,
      icon: config.icon,
    };
  });

  const menuContent = (
    <div
      ref={menuRef}
      className="position-fixed bg-white rounded-4 shadow-xl border overflow-hidden"
      style={{
        width: '280px',
        zIndex: 9999,
        top: menuPosition.top !== 'auto' ? `${menuPosition.top}px` : 'auto',
        left: `${menuPosition.left}px`,
        bottom: menuPosition.bottom !== 'auto' ? `${menuPosition.bottom}px` : 'auto',
        maxHeight: '80vh',
        overflowY: 'auto'
      }}
    >
      <div className="p-2">
        <div className="px-3 py-2 text-secondary small fw-bold text-uppercase tracking-wider">
          Basic Questions
        </div>

        {questionOptions.map(({ type, label, icon }) => (
          <button
            key={type}
            onClick={() => handleAddQuestion(type)}
            className="w-100 d-flex align-items-center gap-3 px-3 py-2 border-0 bg-transparent hover-bg-light text-start transition-colors rounded-2"
          >
            <span className="fs-5 d-flex align-items-center justify-content-center bg-light rounded p-1" style={{ width: '32px', height: '32px' }}>
              {icon}
            </span>
            <span className="fw-medium text-dark">{label}</span>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <button
        ref={buttonRef}
        onClick={toggleMenu}
        className={`btn d-flex align-items-center justify-content-center gap-2 shadow-lg transition-all ${isOpen ? 'rotate-45' : ''}`}
        style={{
          width: isOpen ? '56px' : 'auto',
          height: '56px',
          borderRadius: '30px',
          padding: isOpen ? '0' : '0 24px',
          background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
          border: 'none',
          color: 'white',
          minWidth: '56px'
        }}
        onMouseEnter={(e) => {
          if (!isOpen) e.currentTarget.style.transform = 'translateY(-2px) scale(1.05)';
        }}
        onMouseLeave={(e) => {
          if (!isOpen) e.currentTarget.style.transform = 'translateY(0) scale(1)';
        }}
      >
        <i className={`fa fa-plus fs-4 ${isOpen ? 'rotate-45' : ''}`} style={{ transition: 'transform 0.3s ease' }} aria-hidden="true"></i>
        {!isOpen && <span className="fw-bold fs-6">Add Question</span>}
      </button>

      {isOpen && createPortal(menuContent, document.body)}
    </>
  );
};

export default QuestionToolbar;
